if (!customElements.get('product-media-gallery-swiper')) {
  class ProductMediaGallerySwiper extends HTMLElement {
    constructor() {
      super();
      this.handleSectionLoad = this.handleSectionLoad.bind(this);
      this.handleModalClosed = this.handleModalClosed.bind(this);
    }

    connectedCallback() {
      this.initSwiper();
      this.subscribeToVariantChange();
      document.body.addEventListener('modalClosed', this.handleModalClosed);
      if (Shopify.designMode) {
        document.addEventListener('shopify:section:load', this.handleSectionLoad);
      }
    }

    disconnectedCallback() {
      this.destroySwipers();
      document.body.removeEventListener('modalClosed', this.handleModalClosed);
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
      if (this.variantChangeUnsubscribe) {
        this.variantChangeUnsubscribe();
        this.variantChangeUnsubscribe = null;
      }
      if (Shopify.designMode) {
        document.removeEventListener('shopify:section:load', this.handleSectionLoad);
      }
    }

    handleModalClosed() {
      if (!this.mainSwiper) return;

      requestAnimationFrame(() => {
        const focused = this.querySelector(':focus');
        if (focused) focused.blur();

        this.mainSwiper.update();
        if (this.thumbSwiper) this.thumbSwiper.update();
      });
    }

    handleSectionLoad(event) {
      const sectionId = this.closest('.shopify-section')?.id.replace('shopify-section-', '');
      if (event.detail.sectionId !== sectionId) return;
      this.destroySwipers();
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
      this.initSwiper();
    }

    destroySwipers() {
      if (this.mainSwiper) {
        this.mainSwiper.destroy(true, true);
        this.mainSwiper = null;
      }
      if (this.thumbSwiper) {
        this.thumbSwiper.destroy(true, true);
        this.thumbSwiper = null;
      }
    }

    subscribeToVariantChange() {
      if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

      this.variantChangeUnsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        const sectionId = this.closest('.shopify-section')?.id.replace('shopify-section-', '');
        if (event.data.sectionId !== sectionId) return;

        const variant = event.data.variant;
        if (!variant?.featured_media?.id || !this.mainSwiper) return;

        const targetMediaId = variant.featured_media.id.toString();
        const slides = this.querySelectorAll('.swiper-product-main .swiper-slide');

        for (let i = 0; i < slides.length; i++) {
          if (slides[i].dataset.mediaId === targetMediaId) {
            this.mainSwiper.slideTo(i);
            break;
          }
        }
      });
    }

    initSwiper() {
      if (typeof Swiper === 'undefined') {
        setTimeout(this.initSwiper.bind(this), 500);
        return;
      }

      const thumbEl = this.querySelector('.swiper-product-thumb');
      const mainEl = this.querySelector('.swiper-product-main');

      if (!mainEl) return;

      if (thumbEl) {
        const isDesktop = window.innerWidth >= 750;
        this.thumbSwiper = new Swiper(thumbEl, {
          direction: isDesktop ? 'vertical' : 'horizontal',
          spaceBetween: 10,
          slidesPerView: 'auto',
          freeMode: false,
          watchSlidesProgress: true,
          breakpoints: {
            750: {
              direction: 'vertical',
              slidesPerView: 'auto',
              freeMode: true,
            }
          }
        });
      }

      const self = this;

      this.mainSwiper = new Swiper(mainEl, {
        spaceBetween: 1,
        autoHeight: true,
        loop: true,
        breakpoints: {
          750: {
            spaceBetween: 10,
          }
        },
        navigation: {
          nextEl: this.querySelector('.swiper-button-next'),
          prevEl: this.querySelector('.swiper-button-prev'),
        },
        thumbs: this.thumbSwiper ? { swiper: this.thumbSwiper } : undefined,
        on: {
          slideChangeTransitionEnd: function () {
            this.updateAutoHeight();
          },
          slideChange: function () {
            if (self.thumbSwiper) {
              self.thumbSwiper.slideTo(this.realIndex);
            }
          }
        }
      });

      if (thumbEl) {
        this.resizeObserver = new ResizeObserver(() => {
          const newIsDesktop = window.innerWidth >= 750;

          if (this.thumbSwiper?.changeDirection) {
            if (newIsDesktop && this.thumbSwiper.params.direction !== 'vertical') {
              this.thumbSwiper.changeDirection('vertical');
            } else if (!newIsDesktop && this.thumbSwiper.params.direction !== 'horizontal') {
              this.thumbSwiper.changeDirection('horizontal');
            }
          }

          if (newIsDesktop) {
            const mainHeight = mainEl.offsetHeight;
            if (mainHeight > 0) {
              thumbEl.style.height = `${mainHeight}px`;
              this.thumbSwiper?.update();
            }
          } else {
            thumbEl.style.height = '';
            this.thumbSwiper?.update();
          }
        });
        this.resizeObserver.observe(mainEl);
      }
    }
  }

  customElements.define('product-media-gallery-swiper', ProductMediaGallerySwiper);
}
